import React, { useEffect, useMemo, useState } from 'react';
import {
  DropOffPointOutlined,
  LaunchUniversal,
  SearchUniversal,
} from '@duxton-web/icons';
import {
  Button,
  Divider,
  Flex,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
} from '@duxton-web/ui';
import { ColumnsType } from '@duxton-web/ui/es/table';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import GrabMap from '../GrabMap';
import {
  GET_GRAB_AREA_DEFAULT_PAGE_SIZE,
  GrabAreaItem,
  GrabAreaMode,
} from './const';
import GrabAreaHierarchySelector from './GrabAreaHierarchySelector';
import { useGetGrabAreaGeoJson } from './hooks';
import Service, { Country } from '@/lib/App';
import { GrabAreaSearchResult } from '@/lib/GrabAreaManagementDTOs';
import { GEO_TOOLS_GRAB_AREA_URL } from '@/utils/url';

import './GrabAreaModal.css';

interface GrabAreaModalProps {
  selectedValues: GrabAreaItem[];
  onChange: (values: GrabAreaItem[]) => void;
  onClose: () => void;
}

const getCountriesMap = (): Map<number, Country> => {
  const countries = Service.cache!.getAllCountries();
  const countriesMap = new Map();
  countries.forEach((c: Country) => {
    if (c.ID != null) {
      countriesMap.set(c.ID, {
        countryCode: c.Code,
        countryID: c.ID,
        name: c.Name,
      });
    }
  });
  return countriesMap;
};

const GrabAreaModal: React.FC<GrabAreaModalProps> = ({
  selectedValues,
  onChange,
  onClose,
}) => {
  const [tempSelectedValues, setTempSelectedValues] =
    useState<GrabAreaItem[]>(selectedValues);
  const [grabAreaMode, setGrabAreaMode] = useState<GrabAreaMode>(
    GrabAreaMode.AREA_NAME,
  );

  const [searchByNameValue, setSearchByNameValue] = useState('');
  const [searchByParentIDValue, setSearchByParentIDValue] = useState<number>();
  const [searchByAreaIDsValue, setSearchByAreaIDsValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(
    GET_GRAB_AREA_DEFAULT_PAGE_SIZE,
  );
  const [areaSearchResult, setAreaSearchResult] =
    useState<GrabAreaSearchResult>();

  const [currentAreaID, setCurrentAreaID] = useState<number>();

  const [mapContainerSize, setMapContainerSize] = useState(0);

  const getGrabAreas = async ({
    page,
    pageSize,
    parentID,
    areaName,
  }: {
    page?: number;
    pageSize?: number;
    parentID?: number;
    areaName?: string;
  }) => {
    try {
      setIsLoading(true);
      const limit = pageSize || currentPageSize;
      const offset = ((page || currentPage) - 1) * limit;
      const areaNameValue =
        areaName !== undefined
          ? areaName
          : grabAreaMode === GrabAreaMode.AREA_NAME
            ? searchByNameValue
            : undefined;
      const parentIDValue =
        parentID !== undefined
          ? parentID
          : grabAreaMode === GrabAreaMode.HIERARCHY
            ? searchByParentIDValue
            : -1;
      const res = await Service.grabAreaManagement.getGrabAreas({
        offset,
        limit,
        areaName: areaNameValue,
        parentID: parentIDValue,
      });
      setAreaSearchResult(res);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const getBatchGrabAreas = async ({ areaIDs }: { areaIDs: number[] }) => {
    try {
      if (areaIDs.length === 0) {
        setAreaSearchResult({ grabAreasInfo: [], total: 0 });
        return;
      }
      setIsLoading(true);
      const res = await Service.grabAreaManagement.batchSearchGrabArea({
        areaIDs,
        limit: areaIDs.length,
      });
      setAreaSearchResult({
        grabAreasInfo: res?.grabAreasInfo || [],
        total: res?.grabAreasInfo?.length || 0,
      });
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  };

  const {
    data: grabAreaGeoJsonData,
    isLoading: isLoadingGrabAreaGeoJson,
    isRefetching: isRefetchingGrabAreaGeoJson,
  } = useGetGrabAreaGeoJson(currentAreaID);

  const columns: ColumnsType<GrabAreaItem> = [
    {
      title: 'Area Name',
      dataIndex: 'areaId',
      key: 'areaId',
      render: (_, record) => (
        <Flex vertical gap={4}>
          <Typography.Body level={3}>{record.areaName}</Typography.Body>
          <Typography.Metadata style={{ color: '#707070' }}>
            {record.areaId}
          </Typography.Metadata>
        </Flex>
      ),
      fixed: 'left',
    },
    {
      title: 'Country',
      dataIndex: 'countryID',
      key: 'countryID',
      render: (_, record) => {
        const countriesMap = getCountriesMap();
        const country = countriesMap.get(record.countryID);
        return country?.name || country?.countryCode;
      },
      width: 100,
    },
    {
      title: 'Layer',
      dataIndex: 'layer',
      key: 'layer',
      width: 100,
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Flex align="center" justify="left" gap={4}>
          <Button
            shape="circle"
            size="small"
            style={{ lineHeight: 0 }}
            type="secondary"
            icon={<DropOffPointOutlined />}
            disabled={
              isLoadingGrabAreaGeoJson ||
              isRefetchingGrabAreaGeoJson ||
              currentAreaID === record.areaId
            }
            onClick={(_) => {
              setCurrentAreaID(record.areaId);
            }}
          />
          <Button
            shape="circle"
            size="small"
            style={{ lineHeight: 0 }}
            type="secondary"
            icon={<LaunchUniversal />}
            onClick={(_) => {
              window.open(
                `${GEO_TOOLS_GRAB_AREA_URL}/detail?areaID=${record.areaId}`,
                '_blank',
              );
            }}
          />
        </Flex>
      ),
      width: 100,
    },
  ];

  const dataSource: GrabAreaItem[] = useMemo(() => {
    if (!areaSearchResult?.grabAreasInfo?.length) {
      return [];
    }
    return areaSearchResult.grabAreasInfo.map((area) => ({
      areaId: area.areaID,
      areaName: area.areaName,
      countryID: area.countryID,
      layer: area.layer,
    }));
  }, [areaSearchResult]);

  useEffect(() => {
    getGrabAreas({ page: 1 });
  }, []);

  const handleSwitchSearchMode = (mode: GrabAreaMode) => {
    setSearchByNameValue('');
    setSearchByParentIDValue(undefined);
    setSearchByAreaIDsValue('');

    setGrabAreaMode(mode);
    setCurrentPage(1);

    if (mode === GrabAreaMode.AREA_NAME) {
      getGrabAreas({ page: 1, areaName: '', parentID: -1 });
    } else if (mode === GrabAreaMode.HIERARCHY) {
      getGrabAreas({ page: 1, parentID: 0, areaName: null });
    }
  };

  const triggerSearchOnPaginationChange = (page: number, pageSize: number) => {
    if (grabAreaMode === GrabAreaMode.AREA_IDS) {
      // Area IDs does not support pagination
      return;
    }
    getGrabAreas({ page, pageSize });
  };

  const renderSearchArea = () => {
    if (grabAreaMode === GrabAreaMode.AREA_NAME) {
      return (
        <>
          <Input
            placeholder="Search for area"
            style={{ width: 200, flex: 1 }}
            value={searchByNameValue}
            onChange={(e) => {
              setSearchByNameValue(e.target.value);
            }}
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(1);
                getGrabAreas({ page: 1 });
              }
            }}
          />
          <Button
            shape="circle"
            style={{ lineHeight: 0 }}
            type="primary"
            icon={<SearchUniversal />}
            disabled={isLoading}
            onClick={() => {
              setCurrentPage(1);
              getGrabAreas({ page: 1 });
            }}
          />
        </>
      );
    }
    if (grabAreaMode === GrabAreaMode.HIERARCHY) {
      return (
        <GrabAreaHierarchySelector
          onChange={(value) => {
            const parentID = value?.length
              ? Number(value[value.length - 1])
              : 0;
            setSearchByParentIDValue(parentID);
            getGrabAreas({ page: 1, parentID });
          }}
        />
      );
    }
    if (grabAreaMode === GrabAreaMode.AREA_IDS) {
      const triggerBatchSearch = () => {
        const normalizedAreaIDs = searchByAreaIDsValue
          .split(',')
          .map((areaID) => areaID.trim())
          .filter((areaID) => areaID !== '')
          .filter((areaID) => !isNaN(Number(areaID)))
          .map(Number);
        const uniqueAreaIDs = Array.from(new Set(normalizedAreaIDs));
        setCurrentPage(1);
        getBatchGrabAreas({ areaIDs: uniqueAreaIDs });
      };

      return (
        <>
          <Input
            placeholder="Search by a list of comma separated area IDs, e.g: 123,456"
            style={{ flex: 1 }}
            value={searchByAreaIDsValue}
            onChange={(e) => {
              setSearchByAreaIDsValue(e.target.value);
            }}
            disabled={isLoading}
            onPressEnter={() => {
              triggerBatchSearch();
            }}
            allowClear
          />
          <Button
            shape="circle"
            style={{ lineHeight: 0 }}
            type="primary"
            icon={<SearchUniversal />}
            disabled={isLoading}
            onClick={() => {
              triggerBatchSearch();
            }}
          />
        </>
      );
    }
  };

  return (
    <Modal
      open
      width="960px"
      okText="Save Change"
      cancelText="Cancel"
      onOk={() => {
        onChange(tempSelectedValues);
        onClose();
      }}
      onCancel={onClose}
    >
      <Flex vertical gap={16}>
        <Flex vertical gap={8}>
          <Typography.Title
            level={4}
            color="#1a1a1a"
            style={{ marginBottom: 0 }}
          >
            Select GrabArea
          </Typography.Title>

          <Typography.Body level={3} style={{ color: '#3d3d3d' }}>
            Search and select one or multiple GrabAreas that you want to add.
          </Typography.Body>
        </Flex>

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong style={{ color: '#3d3d3d' }}>
            Search Areas
          </Typography.Body>

          <Flex vertical gap={10}>
            <Flex gap={8}>
              <Select
                style={{ minWidth: 120 }}
                options={Object.keys(GrabAreaMode).map((k) => ({
                  label: GrabAreaMode[k],
                  value: GrabAreaMode[k],
                }))}
                value={grabAreaMode}
                onChange={(value) => {
                  handleSwitchSearchMode(value);
                }}
                disabled={isLoading}
              />
              {renderSearchArea()}
            </Flex>
            <PanelGroup autoSaveId="grabAreaSelector" direction="horizontal">
              <Panel defaultSize={65} minSize={35} maxSize={65}>
                <Table
                  loading={isLoading}
                  rowKey={'areaId'}
                  scroll={{ y: 363, x: 450 }}
                  style={{
                    borderRadius: 12,
                    border: '1px solid #BFBFBF',
                    paddingBottom: 8,
                    height: 466,
                  }}
                  pagination={{
                    size: 'small',
                    style: {
                      margin: '8px 8px 0px 0px',
                      flexWrap: 'nowrap',
                    },
                    pageSize: currentPageSize,
                    current: currentPage,
                    total: areaSearchResult?.total,
                    onChange(page, pageSize) {
                      if (pageSize !== currentPageSize) {
                        setCurrentPageSize(pageSize);
                        setCurrentPage(1);
                        triggerSearchOnPaginationChange(1, pageSize);
                      } else {
                        setCurrentPage(page);
                        triggerSearchOnPaginationChange(page, pageSize);
                      }
                    },
                    showSizeChanger: true,
                    showLessItems: true,
                    pageSizeOptions: [10, 20, 50],
                  }}
                  rowSelection={{
                    onSelect: (record, selected) => {
                      if (!selected) {
                        setTempSelectedValues(
                          tempSelectedValues.filter(
                            (v) => v.areaId !== record.areaId,
                          ),
                        );
                      } else {
                        setTempSelectedValues([...tempSelectedValues, record]);
                      }
                    },
                    onSelectAll: (selected, _, changeRows) => {
                      if (selected) {
                        setTempSelectedValues([
                          ...tempSelectedValues,
                          ...changeRows,
                        ]);
                      } else {
                        setTempSelectedValues(
                          tempSelectedValues.filter(
                            (v) =>
                              !changeRows
                                .map((r) => r.areaId)
                                .includes(v.areaId),
                          ),
                        );
                      }
                    },
                    selectedRowKeys: tempSelectedValues.map((v) => v.areaId),
                  }}
                  dataSource={dataSource}
                  columns={columns}
                />
              </Panel>

              <PanelResizeHandle className="resize-handle-container">
                <Divider
                  className="areaModal-resize-handle"
                  type="vertical"
                  style={{ height: '100%' }}
                  orientationMargin={8}
                />
              </PanelResizeHandle>

              <Panel
                minSize={35}
                maxSize={65}
                onResize={(size) => setMapContainerSize(size)}
              >
                <GrabMap
                  geoJson={grabAreaGeoJsonData}
                  panelSize={mapContainerSize}
                />
              </Panel>
            </PanelGroup>
          </Flex>
        </Flex>

        <Flex vertical gap={8}>
          <Typography.Body level={2} strong style={{ color: '#3d3d3d' }}>
            Selected Items{' '}
            <Tag
              bordered={false}
              style={{
                borderRadius: 16,
                backgroundColor: '#F5F5F5',
                color: '#575757',
              }}
              size="xsmall"
            >
              {tempSelectedValues.length}
            </Tag>
          </Typography.Body>
          <Flex gap={4} wrap="wrap">
            {tempSelectedValues.map((grabArea) => (
              <Tag
                key={grabArea.areaId}
                closable
                color={currentAreaID === grabArea.areaId ? 'green' : 'white'}
                size="small"
                onClose={() => {
                  setTempSelectedValues(
                    tempSelectedValues.filter(
                      (v) => v.areaId !== grabArea.areaId,
                    ),
                  );
                }}
                onClick={() => {
                  setCurrentAreaID(grabArea.areaId);
                }}
                style={{ margin: 0, cursor: 'pointer' }}
              >
                {grabArea.areaName} ({grabArea.areaId})
              </Tag>
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default GrabAreaModal;
